import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SharedReportPage() {
  const { slug } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    fetch(`${API}/api/share/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('Report not found');
        return r.json();
      })
      .then(data => {
        setReport(data.full_report || data);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading) return (
    <div style={{ minHeight: '100dvh', background: '#F0F1F7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700, color: '#0A0F2E', marginBottom: 8 }}>Loading report...</div>
        <div style={{ fontSize: 13, color: 'rgba(10,10,26,0.5)' }}>Please wait while we fetch your audit results.</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100dvh', background: '#F0F1F7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: '#0A0F2E', marginBottom: 8 }}>Report not found</div>
        <div style={{ fontSize: 13, color: 'rgba(10,10,26,0.5)', lineHeight: 1.6 }}>This audit report may have expired or the link is invalid. Please contact the hotel or request a new audit.</div>
      </div>
    </div>
  );

  if (!report) return null;

  // Prefer the slug-based endpoint — it looks up the record by share_slug
  // (single indexed field) and renders from full_report on disk miss. This
  // avoids the older filename-based route's hyphen/encoding pitfalls and
  // works reliably across Emergent redeploys even for records that predate
  // report_file being persisted in full_report.
  //
  // Forward `?tab=<id>` from the share URL into the iframe so deep-links
  // (e.g. the email's "View prioritised action plan" CTA hitting
  // /share/{slug}?tab=action) open the correct report tab on load.
  const tabParam = new URLSearchParams(window.location.search).get('tab');
  const baseIframe = slug
    ? `${API}/api/report-by-slug/${slug}`
    : (report.report_url ? `${API}${report.report_url}` : null);
  const iframeSrc = baseIframe && tabParam
    ? `${baseIframe}?tab=${encodeURIComponent(tabParam)}`
    : baseIframe;

  return (
    <div style={{ minHeight: '100dvh', background: '#F0F1F7' }} data-testid="shared-report-page">
      {/* Header */}
      <div style={{ background: '#0A0F2E', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="https://cdn.prod.website-files.com/686e2126992edd7b040ec218/69b4f893406935517896aa07_logo.avif" alt="dhi" style={{ height: 22, opacity: 0.9 }} />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', borderLeft: '1px solid rgba(255,255,255,0.12)', paddingLeft: 12 }}>AI Visibility Audit</span>
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
          {report.hotel_name} &middot; {report.url}
        </div>
      </div>

      {/* Report iframe */}
      {iframeSrc ? (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
          <iframe
            src={iframeSrc}
            title="Audit Report"
            style={{ width: '100%', minHeight: 'calc(100dvh - 120px)', border: '1px solid rgba(10,10,26,0.08)', borderRadius: 12, background: '#fff' }}
            data-testid="shared-report-iframe"
          />
        </div>
      ) : (
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px' }} data-testid="shared-report-partial-summary">
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(10,10,26,0.08)', padding: 32 }}>
            {/* Honest framing — without this, the user sees the score panel
                below and assumes the full report is still loading. Explain
                upfront that this is the headline-only fallback. */}
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 700, color: '#0A0F2E', marginBottom: 6 }}>
              Full report unavailable
            </div>
            <div style={{ fontSize: 13, color: 'rgba(10,10,26,0.6)', lineHeight: 1.55, marginBottom: 20 }}>
              The audit completed but the detailed report couldn't be loaded.
              Here's the headline result — email{' '}
              <a href="mailto:hello@dhihospitality.com" style={{ color: '#2D4DE0' }}>hello@dhihospitality.com</a>
              {' '}to have the full report regenerated.
            </div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: '#0A0F2E', marginBottom: 4 }}>{report.hotel_name}</div>
            <div style={{ fontSize: 13, color: 'rgba(10,10,26,0.4)', marginBottom: 16 }}>{report.url}</div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
              <div style={{ background: '#F5F6FA', borderRadius: 10, padding: '14px 20px', textAlign: 'center', flex: 1 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 36, fontWeight: 900, color: '#0A0F2E' }}>{Math.round(report.final_score || 0)}</div>
                <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)' }}>AI Visibility Score</div>
              </div>
              <div style={{ background: '#F5F6FA', borderRadius: 10, padding: '14px 20px', textAlign: 'center', flex: 1 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 36, fontWeight: 900, color: '#0A0F2E' }}>
                  {report.grade || 'F'}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)' }}>Grade</div>
              </div>
            </div>
            {report.grade_label && <div style={{ fontSize: 13, color: 'rgba(10,10,26,0.5)', textAlign: 'center' }}>{report.grade_label}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
